import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useClientScope } from "../../../../../shared/contexts";
import {
  useReports,
  useCreateReport,
  useDeleteReport,
} from "../hooks/useReporting";
import { GenerateWizard } from "../components/GenerateWizard";
import { generateReport } from "../utils/generateReport";
import { ROUTES } from "../../../../../shared/utils";

/**
 * Reporting landing page: lists saved reports and creates new ones. Opening a
 * report navigates to the drag-and-drop editor.
 */
export const Reporting: React.FC = () => {
  const navigate = useNavigate();
  const { effectiveClientId, isCentralAdmin, clients } = useClientScope();
  const { data: reports, isLoading, isError } = useReports(effectiveClientId);

  const clientName = (id: string | null): string | null => {
    if (!id) return null;
    return clients.find((c) => c.id === id)?.name ?? null;
  };
  const createReport = useCreateReport();
  const deleteReport = useDeleteReport();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const handleCreate = async () => {
    const report = await createReport.mutateAsync({
      title: "Untitled report",
      client_id: effectiveClientId,
    });
    navigate(`${ROUTES.REPORTING}/${report.id}`);
  };

  const handleGenerate = async (input: {
    templates: Parameters<typeof generateReport>[0]["templates"];
    filters: Parameters<typeof generateReport>[0]["filters"];
    periodLabel: string;
    clientId: string | null;
  }) => {
    const title = `Wellbeing Report — ${input.periodLabel}`;
    const blocks = generateReport({
      templates: input.templates,
      filters: input.filters,
      title,
      periodLabel: input.periodLabel,
    });
    const report = await createReport.mutateAsync({
      title,
      client_id: input.clientId,
      layout: { blocks },
    });
    setWizardOpen(false);
    navigate(`${ROUTES.REPORTING}/${report.id}`);
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await deleteReport.mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Reporting
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Build report packs with charts and narrative, then export to PDF or
            present with narration.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={createReport.isPending}
          >
            Blank report
          </Button>
          <Button
            variant="contained"
            startIcon={
              createReport.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesomeIcon />
              )
            }
            onClick={() => setWizardOpen(true)}
            disabled={createReport.isPending}
          >
            Generate report
          </Button>
        </Stack>
      </Stack>

      <GenerateWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onGenerate={handleGenerate}
      />

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && <Alert severity="error">Couldn't load reports.</Alert>}

      {!isLoading && !isError && (reports?.length ?? 0) === 0 && (
        <Alert severity="info">
          No reports yet. Create your first report to get started.
        </Alert>
      )}

      <Grid container spacing={2}>
        {reports?.map((report) => (
          <Grid key={report.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card variant="outlined">
              <CardActionArea
                onClick={() => navigate(`${ROUTES.REPORTING}/${report.id}`)}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <AssessmentIcon color="primary" />
                    <Tooltip title="Delete report">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(report.id, e)}
                          disabled={deletingId === report.id}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  {isCentralAdmin && !effectiveClientId && report.client_id && (
                    <Chip
                      label={clientName(report.client_id) ?? report.client_id}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, maxWidth: "100%" }}
                    />
                  )}
                  <Typography variant="h6" fontWeight={600} sx={{ mt: 1 }} noWrap>
                    {report.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, minHeight: 20 }}
                    noWrap
                  >
                    {report.description || "No description"}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Updated {dayjs(report.updated_at).format("D MMM YYYY")}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {report.layout?.blocks?.length ?? 0} blocks
                    </Typography>
                    {report.audio_status === "ready" && (
                      <Chip label="Narrated" size="small" color="success" variant="outlined" />
                    )}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
